import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Gift, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { money } from "@/lib/format";

// Add a gift card line to the POS ticket: admin-configured presets or a custom amount.
export default function GiftCardSellModal({ client, onAdd, onClose }) {
  const { data: opts } = useQuery({ queryKey: ["gift-card-options"], queryFn: () => api.get("/gift-cards/options").then((r) => r.data) });
  const [amount, setAmount] = useState("");
  const [f, setF] = useState({ purchaser_name: client ? `${client.first_name || ""} ${client.last_name || ""}`.trim() : "", purchaser_email: client?.email || "", recipient_name: "", recipient_email: "", message: "" });
  const amt = Number(amount);
  const preset = (opts?.denominations || []).map(Number);
  const valid = amt > 0 && (preset.includes(amt) || (opts?.allow_custom && amt >= opts.min_amount && amt <= opts.max_amount));
  const add = () => {
    if (!valid) { toast.error(`Choose a preset or a custom amount between ${money(opts?.min_amount)} and ${money(opts?.max_amount)}.`); return; }
    onAdd({ kind: "giftcard", ref_id: "", title: `Gift card (${money(amt)})`, unit_price: amt, qty: 1, gift: f });
    onClose();
  };
  const inp = "eyw-input w-full !py-2 text-sm";
  return (
    <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm grid place-items-center px-4" data-testid="pos-giftcard-modal">
      <div className="w-full max-w-md bg-[#F9F7F3] rounded-3xl p-6 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center"><h3 className="font-serif text-xl flex items-center gap-2"><Gift size={18} className="text-[#D98E73]" /> Sell a gift card</h3><button onClick={onClose} data-testid="pos-giftcard-close"><X size={18} /></button></div>
        <div className="flex flex-wrap gap-2 mt-4" data-testid="pos-giftcard-denoms">
          {preset.map((d) => (
            <button key={d} onClick={() => setAmount(String(d))} data-testid={`pos-giftcard-denom-${d}`}
              className={`rounded-full px-4 py-2 text-sm border transition-colors ${amt === d ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#144F75]/25 text-[#144F75] hover:bg-[#144F75]/[0.06]"}`}>{money(d)}</button>
          ))}
        </div>
        {opts?.allow_custom && (
          <div className="mt-3">
            <label className="text-xs text-[#4A5D6B]">Custom amount ({money(opts.min_amount)}–{money(opts.max_amount)})</label>
            <input type="number" min={opts.min_amount} max={opts.max_amount} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pos-giftcard-custom" className={inp} />
          </div>
        )}
        <p className="eyw-label mt-4">Purchaser</p>
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="Name" value={f.purchaser_name} onChange={(e) => setF({ ...f, purchaser_name: e.target.value })} data-testid="pos-giftcard-purchaser-name" className={inp} />
          <input placeholder="Email (gets the code)" value={f.purchaser_email} onChange={(e) => setF({ ...f, purchaser_email: e.target.value })} data-testid="pos-giftcard-purchaser-email" className={inp} />
        </div>
        <p className="eyw-label mt-3">Recipient (optional)</p>
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="Name" value={f.recipient_name} onChange={(e) => setF({ ...f, recipient_name: e.target.value })} data-testid="pos-giftcard-recipient-name" className={inp} />
          <input placeholder="Email" value={f.recipient_email} onChange={(e) => setF({ ...f, recipient_email: e.target.value })} data-testid="pos-giftcard-recipient-email" className={inp} />
        </div>
        <input placeholder="Message (optional)" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="pos-giftcard-message" className={`${inp} mt-2`} />
        <button onClick={add} disabled={!valid} data-testid="pos-giftcard-add" className="eyw-btn w-full mt-4 disabled:opacity-50">Add {amt > 0 ? money(amt) : ""} gift card to sale</button>
        <p className="text-[11px] text-[#8FA89B] mt-2">A unique EYW-XXXX-XXXX-XXXX code is created the moment the sale is paid and emailed to the purchaser.</p>
      </div>
    </div>
  );
}
